import { ElementRef, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { fromEvent, Subject, auditTime, takeUntil } from 'rxjs';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

import { SpyOptions } from '../_interfaces/spy-options.interface';
import { SpyTarget } from '../_interfaces/spy-target.interface';

@Injectable({
  providedIn: 'root'
})
export class ScrollSpyService {
  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  private stopSpying$ = new Subject();
  private activeSpyTarget$ = new Subject<string>();
  private spyTargets: SpyTarget[] = [];
  private thresholdTop = 0;
  private thresholdBottom = 0;
  private isSpying = false;

  get activeSpyTarget() {
    return this.activeSpyTarget$.asObservable();
  }

  spy({ thresholdTop = 0, thresholdBottom = 0 }: SpyOptions = {}): void {
    // this is to prevent duplicate listeners
    if (this.isSpying) {
      return;
    }

    this.isSpying = true;
    this.thresholdTop = thresholdTop;
    this.thresholdBottom = thresholdBottom;

    fromEvent(window, 'scroll', { passive: true })
      .pipe(auditTime(10), takeUntil(this.stopSpying$))
      .subscribe(() => this.checkActiveElement());

    fromEvent(window, 'resize', { passive: true })
      .pipe(auditTime(300), takeUntil(this.stopSpying$))
      .subscribe(() => this.checkActiveElement());

    this.checkActiveElement();
  }

  stopSpying(): void {
    this.stopSpying$.next(undefined);
    this.spyTargets = [];
    this.isSpying = false;
  }

  addTarget(target: SpyTarget): void {
    this.spyTargets.push({ ...target });
    this.checkActiveElement();
  }

  removeTarget(target: string): void {
    this.spyTargets = this.spyTargets.filter(
      (spyTarget) => target !== spyTarget.name,
    );
    this.checkActiveElement();
  }

  isElementActive(element: ElementRef, currentActiveElement?: ElementRef): boolean {
    if (element === undefined) {
      return false;
    }

    const targetOffsetTop = this.getElementOffsetTop(element);
    if (currentActiveElement != null && this.getElementOffsetTop(currentActiveElement) > targetOffsetTop) {
      return false;
    }

    const targetHeight = element?.nativeElement.getBoundingClientRect().height;
    return this.isElementInsideWindow(targetHeight, targetOffsetTop);
  }

  private checkActiveElement(): void {
    let activeTarget: SpyTarget = null;

    if (isPlatformBrowser(this.platformId)) {
      for (const target of this.spyTargets) {
        const activeElement = activeTarget != null ? activeTarget.element : null;
        if (this.isElementActive(target.element, activeElement)) {
          activeTarget = target;
        }
      }
    }

    this.activeSpyTarget$.next(activeTarget ? activeTarget.name : null);
  }

  private getElementOffsetTop(element: ElementRef): number {
    const elementBox = element?.nativeElement.getBoundingClientRect();
    const scrollTop = Math.max(
      window.pageYOffset,
      this.document.documentElement.scrollTop,
      this.document.body.scrollTop,
    );

    return elementBox.top + scrollTop;
  }

  private isElementInsideWindow(elementHeight: number, elementOffsetTop: number): boolean {
    const scrollTop = Math.max(
      window.pageYOffset,
      this.document.documentElement.scrollTop,
      this.document.body.scrollTop,
    );
    const viewportHeight = Math.max(
      this.document.documentElement.clientHeight,
      window.innerHeight || 0,
    );
    const elementBottom = elementOffsetTop + elementHeight;
    const viewportBottom = scrollTop + viewportHeight - this.thresholdBottom;
    const viewportTop = scrollTop + this.thresholdTop;

    // target bottom inside viewport or target top inside viewport or (target bottom below viewport and target top above viewport)
    return (
      (elementBottom > viewportTop && elementBottom < viewportBottom) ||
      (elementOffsetTop > viewportTop && elementOffsetTop < viewportBottom) ||
      (elementOffsetTop < viewportTop && elementBottom > viewportBottom)
    );
  }
}
